/**
 * medialPath — ADR-023 §3: **walk a digit's own tube, and say how far along it
 * everything is.** The geometry the placement has been missing.
 *
 * ONE responsibility: given a grid and two points, return the path from one to
 * the other that stays in the MIDDLE of the occupied volume, with an arclength
 * for every station. It decides nothing about anatomy — no digit names, no
 * proportions, no joints. `place-digits` walks this; only it knows what a
 * knuckle is.
 *
 * WHY THIS EXISTS. `placeDigits` puts interior joints on the straight chord
 * from MCP to tip, on the premise its own header states: *"rest-pose fingers are
 * straight so the straight span is the sound path"*. That is a claim about the
 * specimen, and it is false for an entire class — generated A-pose characters
 * routinely bind with the hand relaxed and the fingers curled, and the chord
 * then cuts the arc. Measured across the committed corpus (ADR-023 §1.2), the
 * placed joints sit up to ELEVEN voxels outside the flesh they are meant to
 * drive, which is a deformation bug and not a cosmetic one: `computeBindWeights`
 * seeds each bone's geodesic field from the voxels its segment passes through,
 * so a bone outside the tube anchors its weights somewhere it does not run.
 *
 * THE ROUTE IS GEODESIC; THE POSITION IS THE CENTROID. Two steps, and they fix
 * different halves:
 *
 *  1. **Route** — the through-volume distance field from `from` (the same BFS
 *     the hand bind and the chest repair both measure with, `geodesicField`),
 *     descended from `to`. A path that exists only through occupied voxels
 *     cannot leave the flesh, and on a curled finger it follows the curl.
 *  2. **Centre** — every station is then re-seated on the CROSS-SECTION
 *     CENTROID: the mean of the occupied voxels in a slab perpendicular to the
 *     local tangent. The descent alone hugs the lattice and drifts to whichever
 *     wall it entered by; the centroid is what puts the bone down the middle of
 *     the tube. Same instrument `limbAxisCentre` and `robustCutCentre` already
 *     use at the wrist — this is that idea applied per station.
 *
 * EACH STATION CARRIES ITS RADIUS, because that is the signal a knuckle makes:
 * a joint is a BULGE, so the cross-section widens there. ADR-023 D1 ("proportion
 * leads, the volume corrects within a bound") consumes it; this module only
 * measures it.
 *
 * UNREACHABLE IS AN ANSWER, NOT A CRASH (ADR-023 §7). A tip the volume cannot
 * reach from the wrist through occupied voxels is a real specimen — a digit
 * pinned on a detached scrap, a mitten whose lobes do not connect at this
 * resolution — and the caller keeps its prior placement and says so. Only
 * wiring bugs throw.
 *
 * Everything is VOXEL-INDEX space. Depends on: hands/voxel-grid,
 * hands/bind-weights (geodesicField — the one BFS), hands/voxel-math.
 * Depended on by: hands/place-digits (ADR-023 T2).
 */
import { KernelError } from '../ir/errors.js';
import { geodesicField } from './bind-weights.js';
import { voxelCoords, voxelIndex, type VolumeGrid, type VoxelPoint } from './voxel-grid.js';
import { add, dist, len, scale, sub } from './voxel-math.js';

/** A cannot-walk condition that is a CALLER bug — fail-loud (ADR-010 §7). */
export class MedialPathError extends KernelError {}

/** One station on the path: where it is, how far along, and how thick the tube is there. */
export interface MedialStation {
  /** Voxel-index position, re-seated on the cross-section centroid. */
  readonly at: VoxelPoint;
  /** Distance along the path from `from`, in voxels, measured on the seated points. */
  readonly arclength: number;
  /**
   * Mean distance from this station to the occupied voxels of its own
   * cross-section — the tube's local half-width. A knuckle is a bulge, so this
   * rises at one; it is the evidence ADR-023 D1's correction reads.
   */
  readonly radius: number;
}

export interface MedialPath {
  readonly stations: readonly MedialStation[];
  /** Total arclength in voxels. */
  readonly length: number;
  /**
   * The point at `fraction` of the total arclength, linearly interpolated
   * between the two stations it falls between. THE WHOLE POINT of the module:
   * a proportion (φ, or D2's thumb ladder) becomes a place inside the tube.
   */
  at(fraction: number): VoxelPoint;
}

export interface MedialPathOptions {
  /**
   * Half-thickness of the cross-section slab, in voxels (default 0.75). Thin
   * enough that a slab is a slice rather than a segment of tube, thick enough
   * that a lattice-aligned tube still has voxels in it.
   */
  readonly slabHalfWidth?: number;
  /**
   * How far out to look for the cross-section, in voxels (default 6). It bounds
   * what a station can be pulled toward: too small and a thick paw's centre is
   * never found, too large and the NEIGHBOURING digit joins the average. The
   * radius reported per station says which happened.
   */
  readonly crossSectionRadius?: number;
}

const SLAB_HALF_WIDTH_DEFAULT = 0.75;
const CROSS_SECTION_RADIUS_DEFAULT = 6;

/** Nearest occupied voxel to a point, or -1 when the grid is empty. */
function snap(p: VoxelPoint, occupied: readonly number[], dims: readonly [number, number, number]): number {
  let best = -1;
  let bestD2 = Infinity;
  for (const v of occupied) {
    const c = voxelCoords(v, dims);
    const d2 = (c[0] - p.x) ** 2 + (c[1] - p.y) ** 2 + (c[2] - p.z) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = v;
    }
  }
  return best;
}

/**
 * Walk the tube from `from` to `to`. Returns `null` when no path through
 * occupied volume exists — a product outcome the caller reports (§7), never an
 * exception.
 */
export function medialPath(
  grid: VolumeGrid,
  from: VoxelPoint,
  to: VoxelPoint,
  options: MedialPathOptions = {},
): MedialPath | null {
  const slabHalf = options.slabHalfWidth ?? SLAB_HALF_WIDTH_DEFAULT;
  const reach = options.crossSectionRadius ?? CROSS_SECTION_RADIUS_DEFAULT;
  if (!(slabHalf > 0) || !(reach > 0)) {
    throw new MedialPathError(`medial path: slabHalfWidth and crossSectionRadius must be > 0 (got ${slabHalf}, ${reach})`);
  }
  for (const [name, p] of [['from', from], ['to', to]] as const) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) {
      throw new MedialPathError(`medial path: ${name} is not finite — a producer bug, not a specimen.`);
    }
  }

  const dims = grid.dims as readonly [number, number, number];
  const occupied = [...grid.occupied].sort((a, b) => a - b);
  if (occupied.length === 0) return null;
  const slot = new Int32Array(dims[0] * dims[1] * dims[2]).fill(-1);
  occupied.forEach((v, i) => {
    slot[v] = i;
  });

  // Both ends snap to the volume: a tip is a SURFACE point and routinely
  // quantizes just outside the last occupied voxel, and the wrist centre is a
  // centroid that can fall in a hollow. Snapping here is not a judgement about
  // whether the pin is on the hand — that check belongs to the caller and
  // already exists (`distanceToOccupied`, ADR-022 §9).
  const startVoxel = snap(from, occupied, dims);
  const endVoxel = snap(to, occupied, dims);
  if (startVoxel < 0 || endVoxel < 0) return null;

  const field = geodesicField([startVoxel], dims, slot, occupied);
  if (!Number.isFinite(field[slot[endVoxel]!]!)) return null; // unreachable through the volume

  // --- descend the field from the far end back to the seed ---------------------
  const route: number[] = [endVoxel];
  let current = endVoxel;
  let guard = occupied.length + 1;
  while (field[slot[current]!]! > 0 && guard-- > 0) {
    const [x, y, z] = voxelCoords(current, dims);
    let next = -1;
    let bestD = field[slot[current]!]!;
    // 26-connected descent: the field is 6-connected (BFS), but stepping
    // diagonally where the field allows it keeps the route from staircasing,
    // and every step is still a voxel inside the volume.
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const ax = x + dx;
          const ay = y + dy;
          const az = z + dz;
          if (ax < 0 || ay < 0 || az < 0 || ax >= dims[0] || ay >= dims[1] || az >= dims[2]) continue;
          const s = slot[voxelIndex(ax, ay, az, dims)]!;
          if (s < 0) continue;
          const d = field[s]!;
          if (d < bestD) {
            bestD = d;
            next = voxelIndex(ax, ay, az, dims);
          }
        }
      }
    }
    if (next < 0) break; // a local minimum that is not the seed — take what we have
    route.push(next);
    current = next;
  }
  route.reverse(); // from → to

  // --- re-seat every station on its cross-section centroid ---------------------
  const raw = route.map((v) => {
    const c = voxelCoords(v, dims);
    return { x: c[0], y: c[1], z: c[2] } as VoxelPoint;
  });
  const seated: VoxelPoint[] = [];
  const radii: number[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const here = raw[i]!;
    const ahead = raw[Math.min(i + 1, raw.length - 1)]!;
    const behind = raw[Math.max(i - 1, 0)]!;
    const tangentRaw = sub(ahead, behind);
    const tl = len(tangentRaw);
    const tangent = tl > 1e-9 ? scale(tangentRaw, 1 / tl) : { x: 0, y: 0, z: 1 };
    const { centre, radius } = crossSection(here, tangent, occupied, dims, slabHalf, reach);
    seated.push(centre);
    radii.push(radius);
  }

  // --- arclength on the SEATED polyline ---------------------------------------
  const stations: MedialStation[] = [];
  let acc = 0;
  for (let i = 0; i < seated.length; i += 1) {
    if (i > 0) acc += dist(seated[i - 1]!, seated[i]!);
    stations.push({ at: seated[i]!, arclength: acc, radius: radii[i]! });
  }
  const total = acc;

  return {
    stations,
    length: total,
    at(fraction) {
      const target = Math.max(0, Math.min(1, fraction)) * total;
      if (stations.length === 1 || total <= 1e-9) return stations[0]!.at;
      for (let i = 1; i < stations.length; i += 1) {
        const a = stations[i - 1]!;
        const b = stations[i]!;
        if (b.arclength < target) continue;
        const span = b.arclength - a.arclength;
        const t = span > 1e-9 ? (target - a.arclength) / span : 0;
        return add(a.at, scale(sub(b.at, a.at), t));
      }
      return stations[stations.length - 1]!.at;
    },
  };
}

/**
 * The centroid and mean half-width of the occupied cross-section through
 * `here`, perpendicular to `tangent` — **restricted to the tube this station is
 * actually in**.
 *
 * THE NEIGHBOUR IS THE WHOLE DIFFICULTY (Codex P2, #96). Two fingers side by
 * side is every hand: the slab through one of them also cuts the other, so
 * averaging "the occupied voxels near this station" puts the centroid in the
 * EMPTY GAP between them — seating a joint outside the flesh, which is the one
 * thing this module exists to prevent. Measured on a two-tube fixture before
 * the fix: the centroid landed in the gap and the reported radius nearly
 * doubled (2.18 against a true ~1.2).
 *
 * So membership is by CONNECTIVITY, not by proximity: flood-fill the slab
 * (26-connected) from the station's own voxel and average only what that
 * reaches. A neighbour four voxels away is near, and not connected within the
 * slice — which is precisely the difference between "nearby flesh" and "this
 * finger".
 *
 * CONNECTIVITY IS ONLY HALF OF IT. Restricting the average to one component
 * fixes *which* voxels are averaged, not *where the average lands*: a non-convex
 * slice has its own mean in a hollow. That second half is handled at the return
 * (Codex P2, #96).
 *
 * Falls back to the station itself when the slab is empty (a one-voxel-thick
 * tube at a coarse resolution), so a thin digit degrades to the route rather
 * than to nothing.
 */
function crossSection(
  here: VoxelPoint,
  tangent: VoxelPoint,
  occupied: readonly number[],
  dims: readonly [number, number, number],
  slabHalf: number,
  reach: number,
): { centre: VoxelPoint; radius: number } {
  const reach2 = reach * reach;
  // --- the slab: occupied voxels near this station and within the slice ------
  const inSlab = new Set<number>();
  let seed = -1;
  let seedD2 = Infinity;
  for (const v of occupied) {
    const c = voxelCoords(v, dims);
    const dx = c[0] - here.x;
    const dy = c[1] - here.y;
    const dz = c[2] - here.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 > reach2) continue;
    // Distance ALONG the tangent decides membership of the slice; distance
    // across it is what the centroid averages.
    if (Math.abs(dx * tangent.x + dy * tangent.y + dz * tangent.z) > slabHalf) continue;
    inSlab.add(v);
    if (d2 < seedD2) {
      seedD2 = d2;
      seed = v;
    }
  }
  if (seed < 0) return { centre: here, radius: 0 };

  // --- the component of the slab this station belongs to ---------------------
  const component: VoxelPoint[] = [];
  const seen = new Set<number>([seed]);
  const queue = [seed];
  while (queue.length > 0) {
    const current = queue.pop()!;
    const [x, y, z] = voxelCoords(current, dims);
    component.push({ x, y, z });
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const ax = x + dx;
          const ay = y + dy;
          const az = z + dz;
          if (ax < 0 || ay < 0 || az < 0 || ax >= dims[0] || ay >= dims[1] || az >= dims[2]) continue;
          const n = voxelIndex(ax, ay, az, dims);
          if (!inSlab.has(n) || seen.has(n)) continue;
          seen.add(n);
          queue.push(n);
        }
      }
    }
  }

  const mean = {
    x: component.reduce((t, m) => t + m.x, 0) / component.length,
    y: component.reduce((t, m) => t + m.y, 0) / component.length,
    z: component.reduce((t, m) => t + m.z, 0) / component.length,
  };

  // THE MEAN OF A COMPONENT NEED NOT LIE INSIDE IT (Codex P2, #96). Connectivity
  // settled WHICH voxels get averaged; it says nothing about where the average
  // LANDS. Any non-convex slice puts its own centre in a hole — a U-shaped cut
  // through a curled finger, an annulus where the slab clips a tube lengthwise,
  // the webbed notch between two digits. The station would then leave the flesh
  // by precisely the mechanism this module exists to prevent, and it would do it
  // in the specimen class the ADR is FOR: a relaxed hand is where the slices go
  // non-convex.
  //
  // So the mean is a PROPOSAL and membership is the check. When it lands in the
  // hollow, fall back to the component voxel nearest it: the most central point
  // that is provably flesh. That is a real loss of sub-voxel precision, and it is
  // the right trade — 0.87 is already the quantization floor here (ADR-023 §1.2,
  // distances measured to voxel centres), so a station is never more precise
  // than a voxel anyway, whereas a station in a cavity is wrong without bound.
  const centre = containsPoint(mean, seen, dims) ? mean : nearest(component, mean);

  // Measured from the station we actually return, not from the proposal — the
  // documented contract is the half-width AT this station, and D1 reads it
  // against its neighbours rather than as an absolute.
  const radius = component.reduce((t, m) => t + dist(m, centre), 0) / component.length;
  return { centre, radius };
}

/** Does `p`'s voxel belong to `members`? The membership test for the centroid. */
function containsPoint(p: VoxelPoint, members: ReadonlySet<number>, dims: readonly [number, number, number]): boolean {
  const x = Math.round(p.x);
  const y = Math.round(p.y);
  const z = Math.round(p.z);
  if (x < 0 || y < 0 || z < 0 || x >= dims[0] || y >= dims[1] || z >= dims[2]) return false;
  return members.has(voxelIndex(x, y, z, dims));
}

/** The member of `points` closest to `p`. Never called with an empty list. */
function nearest(points: readonly VoxelPoint[], p: VoxelPoint): VoxelPoint {
  let best = points[0]!;
  let bestD2 = Infinity;
  for (const m of points) {
    const d2 = (m.x - p.x) ** 2 + (m.y - p.y) ** 2 + (m.z - p.z) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = m;
    }
  }
  return best;
}
